import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Table, Spinner, Button, Input } from "reactstrap";
import {
  getAdminDashboardApi,
  getAnalyticsApi,
  getCoordinatorPerformanceApi,
  getKpiComparisonApi,
  getMonthlyRevenueApi,
} from "../../api/apiUrlConst";
import CountUp from "react-countup";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { useTheme } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

interface DashboardStats {
  totalEnquiries: number;
  totalJoined: number;
  newEnq: number;
  totalFollowUps: number;
  totalRevenue: number;
  todayPayments: number;
  totalPendingAmount: number;
}

const COLORS = ["#0d6efd", "#198754", "#dc3545", "#ffc107", "#6f42c1"];

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [performance, setPerformance] = useState<any[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [chartType, setChartType] = useState<"pie" | "bar">("pie");
  const [kpi, setKpi] = useState<any>(null);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === "dark";
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    loadDashboard();
  }, []);



  const loadDashboard = async () => {
    try {
      const dashboardData = await getAdminDashboardApi();
      const perfData = await getCoordinatorPerformanceApi();
      const revenueData = await getMonthlyRevenueApi();
      const kpiData = await getKpiComparisonApi();

      setStats(dashboardData);
      setPerformance(perfData);
      setKpi(kpiData);



      setMonthlyRevenue(revenueData);

    } catch (error: any) {
      toast.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Format Monthly Revenue (Month Name)
  const formattedRevenue = monthlyRevenue.map((item: any) => ({
    month: new Date(item.year, item.month - 1).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    }),
    total: item.total,
  }));

  const loadAnalytics = async () => {
    if (!fromDate || !toDate) return;

    try {
      const data = await getAnalyticsApi(fromDate, toDate);
      setAnalytics(data);
    } catch (error: any) {
      toast.error("Failed to load analytics");
      console.error(error);
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(performance);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Performance");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, "CoordinatorPerformance.xlsx");
  };

  const calculateChange = (current?: number, last?: number): number => {
    if (current === undefined || last === undefined) return 0;

    if (last === 0) {
      return current > 0 ? 100 : 0;
    }

    const result = ((current - last) / last) * 100;
    return Number(result.toFixed(1));
  };


  if (loading) return <Spinner />;

  return (
    <>
      <div
        className="p-4 mb-4"
        style={{
          background: "linear-gradient(90deg,#374151, #093d92)",
          borderRadius: "16px",
          color: "#fff",
        }}
      >
        <h3 className="fw-bold mb-1">Admin Analytics Dashboard</h3>
        <small>Monitor revenue, enquiries and performance metrics</small>
      </div>
      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        {[
          { label: "Total Enquiries", value: stats?.totalEnquiries || 0, color: "#3b82f6", icon: "📋" },
          { label: "Total Joined", value: stats?.totalJoined || 0, color: "#16a34a", icon: "🎓" },
          { label: "New Enquiries", value: stats?.newEnq || 0, color: "#16a34a", icon: "📜" },
          { label: "Followup", value: stats?.totalFollowUps || 0, color: "#16a34a", icon: "🔁" },
          { label: "Total Revenue", value: stats?.totalRevenue || 0, color: "#0d6efd", icon: "💰", rupee: true },
          { label: "Pending Amount", value: stats?.totalPendingAmount || 0, color: "#dc2626", icon: "⚠", rupee: true },
        ].map((card, index) => (
          <Col md={2} key={index}>
            <Card
              className="border-0 shadow-sm h-100"
              style={{ borderRadius: "16px" }}
            >
              <CardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">{card.label}</div>
                  <h3
                    className="fw-bold mb-0"
                    style={{ color: card.color }}
                  >
                    {card.rupee && "₹ "}
                    <CountUp
                      end={card.value}
                      duration={1.5}
                      separator=","
                    />
                  </h3>
                </div>

                <div
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    background: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                  }}
                >
                  {card.icon}
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "16px" }}>
  <CardBody>
    <Row className="g-3 align-items-end">
      <Col md={4}>
        <label className="fw-semibold">From Date</label>
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded-pill shadow-sm"
        />
      </Col>

      <Col md={4}>
        <label className="fw-semibold">To Date</label>
        <Input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rounded-pill shadow-sm"
        />
      </Col>

      <Col md={4}>
        <Button
          className="w-100 rounded-pill"
          style={{
            background: "linear-gradient(90deg,#374151, #093d92)",
            border: "none",
          }}
          onClick={loadAnalytics}
        >
          Apply Filter
        </Button>
      </Col>
    </Row>
  </CardBody>
</Card> */}
      {analytics && (
        <Row className="g-4 mb-4">
          {[
            {
              title: "Filtered Enquiries",
              value: analytics.totalEnquiries,
              color: "#3b82f6",
              icon: "📋",
            },
            {
              title: "Filtered Revenue",
              value: `₹ ${analytics.totalRevenue?.toLocaleString()}`,
              color: "#16a34a",
              icon: "💰",
            },
            {
              title: "Filtered Joined",
              value: analytics.joined,
              color: "#f59e0b",
              icon: "🎓",
            },
          ].map((item, index) => (
            <Col md={4} key={index}>
              <Card
                className="border-0 shadow-sm h-100"
                style={{
                  borderRadius: "18px",
                  background: "#ffffff",
                }}
              >
                <CardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">{item.title}</div>
                    <h3
                      className="fw-bold mb-0"
                      style={{ color: item.color }}
                    >
                      {item.value}
                    </h3>
                  </div>

                  <div
                    style={{
                      width: 55,
                      height: 55,
                      borderRadius: "50%",
                      background: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                    }}
                  >
                    {item.icon}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="fw-semibold mb-0">Performance Summary</h5>

        <Button
          className="rounded-pill px-4"
          style={{
            background: "linear-gradient(90deg,#16a34a,#22c55e)",
            border: "none",
            fontWeight: 600,
          }}
          onClick={exportToExcel}
        >
          📥 Export Performance
        </Button>
      </div>
      <Row className="g-4 mb-4">
        {[
          {
            title: "Revenue Growth",
            value: calculateChange(
              kpi?.currentRevenue,
              kpi?.lastRevenue
            ),
            icon: "💰",
          },
          {
            title: "Enquiry Growth",
            value: calculateChange(
              kpi?.currentEnquiries,
              kpi?.lastEnquiries
            ),
            icon: "📊",
          },
          {
            title: "Joined Growth",
            value: calculateChange(
              kpi?.currentJoined,
              kpi?.lastJoined
            ),
            icon: "🎓",
          },
        ].map((item, index) => {
          const isPositive = item.value >= 0;

          return (
            <Col md={4} key={index}>
              <Card
                className="border-0 shadow-sm h-100"
                style={{ borderRadius: "18px", transition: "0.3s" }}
              >
                <CardBody className="d-flex justify-content-between align-items-center">

                  {/* Left Content */}
                  <div>
                    <div className="text-muted small">
                      {item.title}
                    </div>

                    <div className="d-flex align-items-center gap-2 mt-2">
                      <h3
                        className={`fw-bold mb-0 ${isPositive ? "text-success" : "text-danger"
                          }`}
                      >
                        {item.value}%
                      </h3>

                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: isPositive ? "#16a34a" : "#dc2626",
                        }}
                      >
                        {isPositive ? "▲ Up" : "▼ Down"}
                      </span>
                    </div>
                  </div>

                  {/* Icon Circle */}
                  <div
                    style={{
                      width: 55,
                      height: 55,
                      borderRadius: "50%",
                      background: isPositive ? "#ecfdf5" : "#fef2f2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                    }}
                  >
                    {item.icon}
                  </div>

                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
      {/* Revenue Line Chart */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "16px" }}>
            <CardBody>
              <h5 className="fw-semibold mb-3">Monthly Revenue Trend</h5>

              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={formattedRevenue}>

                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis dataKey="month" />
                  <YAxis />

                  <Tooltip formatter={(value: any) => `₹ ${value}`} />

                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#0d6efd"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card className="shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  {chartType === "pie"
                    ? "Coordinator Revenue Distribution"
                    : "Coordinator Conversion Rate"}
                </h5>

                <div>
                  <Button
                    size="sm"
                    color={chartType === "pie" ? "primary" : "secondary"}
                    className="me-2"
                    onClick={() => setChartType("pie")}
                  >
                    Pie
                  </Button>

                  <Button
                    size="sm"
                    color={chartType === "bar" ? "primary" : "secondary"}
                    onClick={() => setChartType("bar")}
                  >
                    Bar
                  </Button>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={350}>
                {chartType === "pie" ? (
                  <PieChart>
                    <Pie
                      data={performance}
                      dataKey="revenue"
                      nameKey="name"
                      outerRadius={120}
                      label
                      isAnimationActive
                      onClick={(data) => navigate(`/admin/performance/${data.name}`)}
                    >
                      {performance.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                ) : (
                  <BarChart data={performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="conversionRate"
                      fill="#0d6efd"
                      radius={[6, 6, 0, 0]}
                      animationDuration={800}
                      onClick={(data) => navigate(`/admin/performance/${data.name}`)}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Coordinator Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "16px" }}>
        <CardBody>
          <h5 className="fw-semibold mb-3">Coordinator Performance</h5>

          <Table
            responsive
            hover
            className="align-middle"
            style={{
              borderCollapse: "separate",
              borderSpacing: "0 8px",
            }}
          >
            <thead>
              <tr style={{ background: "#eef2f7" }}>
                <th>Name</th>
                <th>Total Assigned</th>
                <th>Joined</th>
                <th>Conversion %</th>
                <th>Revenue</th>
                <th>Pending</th>
              </tr>
            </thead>

            <tbody>
              {performance.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    background: "#fff",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                  }}
                >
                  <td className="fw-semibold">{item.name}</td>
                  <td>{item.totalAssigned}</td>
                  <td className="text-success fw-semibold">{item.joined}</td>
                  <td>{item.conversionRate}%</td>
                  <td className="fw-semibold">₹ {item.revenue}</td>
                  <td className="text-danger fw-semibold">₹ {item.pending}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default Dashboard;